import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Divider,
  IconButton,
  Collapse,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";

const FareSummary = () => {
  const location = useLocation();
  const bookingData = location.state?.finalBookingData || location.state || {};
  const navigate = useNavigate();
  const [fareExpanded, setFareExpanded] = useState(false);
  const [tripExpanded, setTripExpanded] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isBooking, setIsBooking] = useState(false);

  useEffect(() => {
    const fetchWalletBalance = async () => {
      try {
        const jwtToken = sessionStorage.getItem("jwtToken");
        const response = await fetch(
          "https://api.ipaisa.co.in/myapp/auth/getwalletbalance",
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          setWalletBalance(data.walletBalance || 0);
        } else {
          toast.error("Failed to fetch wallet balance");
        }
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
        toast.error("Error fetching wallet balance");
      } finally {
        setLoading(false);
      }
    };

    fetchWalletBalance();
  }, []);

  const handleAddMoneyClick = () => {
    navigate("/addmoney");
  };

  const prepareBookApiPayload = () => {
    return {
      ResultIndex: bookingData.resultIndex,
      TraceId: bookingData.traceId,
      BoardingPointId: bookingData.selectedBoardingPoint?.CityPointIndex,
      DroppingPointId: bookingData.selectedDroppingPoint?.CityPointIndex,
      Passenger: bookingData.passengerDetails?.map((passenger, index) => ({
        LeadPassenger: index === 0,
        PassengerId: index,
        Title: passenger.gender === "Male" ? "Mr." : "Ms.",
        Address: passenger.address,
        Age: parseInt(passenger.age),
        Email: passenger.email,
        FirstName: passenger.name.split(" ")[0],
        Gender: passenger.gender === "Male" ? 1 : 2,
        IdNumber: null,
        IdType: null,
        LastName: passenger.name.split(" ").slice(1).join(" "),
        Phoneno: passenger.contact,
        Seat: passenger.seatData,
      })),
    };
  };

  const handlePayNow = async () => {
    setIsBooking(true);
    try {
      const jwtToken = sessionStorage.getItem("jwtToken");
      const payload = prepareBookApiPayload();

      const response = await axios.post(
        "https://api.ipaisa.co.in/myapp/bus/book",
        payload,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const bookingResult = response.data;
      console.log("Booking API response:", bookingResult);

      if (!bookingResult) {
        toast.error("No data received from booking API.");
        return;
      }

      if (bookingResult.status === "success") {
        if (bookingResult.bookingDetails?.responseStatus === 1) {
          // Success case
          toast.success("Booking successful!", {
            onClose: () => {
              navigate("/BusBookingTxnReport");
            },
          });
        } else {
          // Failure case with error message
          const errorMessage =
            bookingResult.bookingDetails?.error?.ErrorMessage ||
            "Booking failed";
          toast.error(errorMessage, {
            onClose: () => {
              navigate("/busbookinglanding");
            },
          });
        }
      } else {
        // Handle other status cases
        const errorMessage =
          bookingResult.message || "Booking failed with unknown error";
        toast.error(errorMessage, {
          onClose: () => {
            navigate("/busbookinglanding");
          },
        });
      }
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Error processing booking");
    } finally {
      setIsBooking(false);
    }
  };

  // Extract passenger names from booking data
  const passengerNames =
    bookingData.passengerDetails?.map(
      (passenger) =>
        `${passenger.name} (Seat: ${passenger.seatData?.SeatName})`
    ) || ["No passenger data"];

  // Parse the block response
  const blockResponse = bookingData.blockResponse?.data
    ? JSON.parse(bookingData.blockResponse.data)
    : null;
  const priceDetails = blockResponse?.BlockResult?.PriceDetails?.[0] || {};
  const seatDetails = bookingData.selectedSeats?.[0] || {};

  return (
    <Box sx={{ marginTop: "4%", padding: "2rem" }}>
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Fare Summary with dropdown */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: "none" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Fare Summary
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setFareExpanded(!fareExpanded)}
                >
                  {fareExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={fareExpanded}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Total Fare
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    ₹{priceDetails.PublishedPrice || bookingData.totalAmount || 0}
                  </Typography>

                  <Box sx={{ ml: 2 }}>
                    <Typography variant="body1">Base Fare</Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      ₹{priceDetails.BasePrice || seatDetails.SeatFare || 0}
                    </Typography>

                    {priceDetails.Tax > 0 && (
                      <>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          Taxes
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          ₹{priceDetails.Tax || 0}
                        </Typography>
                      </>
                    )}

                    {priceDetails.Discount > 0 && (
                      <>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          Discount
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          -₹{priceDetails.Discount || 0}
                        </Typography>
                      </>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Amount To Be Paid
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    ₹{bookingData.totalAmount || 0}
                  </Typography>
                </Box>
              </Collapse>

              {!fareExpanded && (
                <Typography variant="h5" sx={{ fontWeight: "bold", mt: 1 }}>
                  Amount To Be Paid: ₹{bookingData.totalAmount || 0}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Trip Details with dropdown */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: "none" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {bookingData.selectedBoardingPoint?.CityPointLocation ||
                    "Bhosari"}{" "}
                  -{" "}
                  {bookingData.selectedDroppingPoint?.CityPointLocation ||
                    "Bandra East"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setTripExpanded(!tripExpanded)}
                >
                  {tripExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              {!tripExpanded && (
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {new Date(
                    bookingData.selectedBoardingPoint?.CityPointTime
                  ).toLocaleString()}{" "}
                  -{" "}
                  {new Date(
                    bookingData.selectedDroppingPoint?.CityPointTime
                  ).toLocaleString()}
                </Typography>
              )}

              <Collapse in={tripExpanded}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {bookingData.selectedBoardingPoint?.CityPointLocation ||
                      "NA"}
                  </Typography>
                  <Typography variant="body2">
                    {bookingData.selectedBoardingPoint?.CityPointAddress || "NA"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Arrival Time:
                    {new Date(
                      bookingData.selectedBoardingPoint?.CityPointTime
                    ).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Contact:{" "}
                    {bookingData.selectedBoardingPoint?.CityPointContactNumber ||
                      "NA"}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {bookingData.selectedDroppingPoint?.CityPointLocation ||
                      "Bandra East"}
                  </Typography>
                  <Typography variant="body2">
                    {bookingData.selectedDroppingPoint?.CityPointName || "NA"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Dropping Time:
                    {new Date(
                      bookingData.selectedDroppingPoint?.CityPointTime
                    ).toLocaleString()}
                  </Typography>
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* Bus Service */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: "none" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                Bus Service
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {bookingData.busDetails?.TravelName || "NA"}
              </Typography>
              <Typography variant="body2">
                {bookingData.busDetails?.BusType || "NA"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Service no. {bookingData.busDetails?.ServiceName || "NA"}
              </Typography>
            </CardContent>
          </Card>

          {/* Travellers */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: "none" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                Travellers
              </Typography>
              {passengerNames.map((name, index) => (
                <Typography key={index} variant="body1">
                  {index + 1}. {name}
                </Typography>
              ))}
            </CardContent>
          </Card>

          {/* Safe Payment */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              100% Safe Payment Process
            </Typography>
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Wallet Card */}
          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "none",
              background: "linear-gradient(45deg, #6a11cb, #2575fc)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                Wallet Balance
              </Typography>
              {loading ? (
                <Typography variant="body1">Loading...</Typography>
              ) : (
                <>
                  <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
                    ₹{walletBalance.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Available for use on this booking
                  </Typography>
                </>
              )}
              <Button
                variant="contained"
                onClick={handleAddMoneyClick}
                sx={{
                  width: "100%",
                  backgroundColor: "white",
                  color: "#6a11cb",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                Add Money
              </Button>
            </CardContent>
          </Card>

          {/* Pay Button */}
          <Button
            variant="contained"
            onClick={handlePayNow}
            disabled={isBooking}
            sx={{
              width: "100%",
              background: "linear-gradient(45deg, #ff416c, #ff4b2b)",
              color: "white",
              fontWeight: "bold",
              padding: "12px",
              fontSize: "1.1rem",
              "&:hover": {
                background: "linear-gradient(45deg, #ff4b2b, #ff416c)",
              },
            }}
          >
            {isBooking ? "Processing..." : "Pay Now"}
          </Button>
        </Grid>
      </Grid>
      <ToastContainer
        position="top-right"
        autoClose={200}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="colored"
      />
    </Box>
  );
};

export default FareSummary;